import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { UserRole } from "@/types/enums";
import { Badge } from "@/components/ui/Badge";
import { NotificationBell } from "@/components/NotificationBell";

const ADMIN_ROLES: string[] = [UserRole.SUPERADMIN, UserRole.DIRECTOR_TECH];

interface NavigationProps {
  children?: ReactNode;
}

export default function Navigation({ children }: NavigationProps) {
  const { user, logout } = useAuth();

  const isAdmin = user && ADMIN_ROLES.includes(user.role);

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-24 items-center justify-between">
          {/* Left: Logo + Nav links */}
          <div className="flex items-center gap-6">
            <Link to="/dashboard" className="flex items-center">
              <img src="/emersal_logo.png" alt="Simulacrum" className="h-20 w-auto" />
            </Link>

            <div className="hidden sm:flex items-center gap-4">
              <Link
                to="/dashboard"
                className="text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Dashboard
              </Link>
              {user && user.role !== UserRole.CLIENT && (
                <Link
                  to="/drills/new"
                  className="text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Nuevo Simulacro
                </Link>
              )}
              <Link
                to="/archive"
                className="text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Archivo
              </Link>
              {isAdmin && (
                <>
                  <span className="text-gray-300">|</span>
                  <Link
                    to="/admin"
                    className="text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    Administración
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Right: User info + Notifications + Logout + mobile slot */}
          <div className="flex items-center gap-4">
            {user && (
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-sm text-gray-700">
                  {user.first_name} {user.last_name}
                </span>
                <Badge>{user.role}</Badge>
              </div>
            )}

            {user && <NotificationBell />}

            <button
              type="button"
              onClick={logout}
              className="text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Cerrar sesión
            </button>

            {children}
          </div>
        </div>
      </div>
    </nav>
  );
}
